import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Film, Folder, ArrowLeft, ShoppingBag, LogOut, Image, MessageCircle, Megaphone, Tv, Upload, Music, Calendar, Camera, Settings, Gamepad2, Sparkles, Star, GripVertical, Globe, Lock, FileText, Video, Package, Building2, Trophy, Bell, Smartphone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

import {
  ShowsTab,
  SeasonsTab,
  VideosTab,
  ContentManagerTab,
  GamesManagerTab,
  BannersTab,
  ShopTab,
  PhrasesTab,
  TickersTab,
  BulkImportTab,
  MusicConfigTab,
  MusicEventsTab,
  BandPhotosTab,
  GamesTab,
  GameFeaturesTab,
  GameGalleryTab,
  GameStoresTab,
  GameTestimonialsTab,
  MediaKitVideosTab,
  MediaKitProductsTab,
  MediaKitLogosTab
} from "@/components/admin";
import ShopBannersTab from "@/components/admin/ShopBannersTab";
import ShopProductOrderTab from "@/components/admin/ShopProductOrderTab";
import { SiteSettingsTab } from "@/components/admin/SiteSettingsTab";
import { MusicThumbnailsTab } from "@/components/admin/MusicThumbnailsTab";
import { PromoBannerTab } from "@/components/admin/PromoBannerTab";
import { NavbarBannerTab } from "@/components/admin/NavbarBannerTab";
import { PrerollTab } from "@/components/admin/PrerollTab";
import { MobileBannerTab } from "@/components/admin/MobileBannerTab";
import { DesktopBannerTab } from "@/components/admin/DesktopBannerTab";
import { SidebarAdsTab } from "@/components/admin/SidebarAdsTab";
import { ChangePasswordModal } from "@/components/admin/ChangePasswordModal";
import MediaKitSuccessCasesTab from "@/components/admin/MediaKitSuccessCasesTab";
import MediaKitConcertPhotosTab from "@/components/admin/MediaKitConcertPhotosTab";
import MediaKitMusicStatsTab from "@/components/admin/MediaKitMusicStatsTab";
import CommentsModerationTab from "@/components/admin/CommentsModerationTab";
import FansTab from "@/components/admin/FansTab";
import AvatarsTab from "@/components/admin/AvatarsTab";
import NotificationsTab from "@/components/admin/NotificationsTab";
import StickersTab from "@/components/admin/StickersTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [shows, setShows] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [videos, setVideos] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [musicConfig, setMusicConfig] = useState(null);
  const [musicEvents, setMusicEvents] = useState([]);
  const [bandPhotos, setBandPhotos] = useState([]);
  const [games, setGames] = useState([]);
  const [shopBanners, setShopBanners] = useState([]);
  const [activeSection, setActiveSection] = useState("videos");
  const [activeSubTab, setActiveSubTab] = useState("shows");
  const [loading, setLoading] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  // Set default sub-tab when section changes
  useEffect(() => {
    if (activeSection === "videos") {
      setActiveSubTab("contenido");
    } else if (activeSection === "promocion") {
      setActiveSubTab("promo-banner");
    } else if (activeSection === "tienda") {
      setActiveSubTab("banners-tienda");
    } else if (activeSection === "musica") {
      setActiveSubTab("config");
    } else if (activeSection === "juegos") {
      setActiveSubTab("gestor");
    } else if (activeSection === "sitio") {
      setActiveSubTab("settings");
    } else if (activeSection === "media-kit") {
      setActiveSubTab("mk-logos");
    } else if (activeSection === "app") {
      setActiveSubTab("stickers");
    } else if (activeSection === "comunidad") {
      setActiveSubTab("comentarios");
    }
  }, [activeSection]);

  const checkAuth = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      setAdminEmail(res.data.email);
      fetchData();
    } catch (error) {
      localStorage.removeItem("admin_token");
      navigate("/login");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    toast.success("Sesión cerrada");
    navigate("/login");
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [showsRes, seasonsRes, videosRes, productsRes, bannersRes, phrasesRes, tickersRes, musicConfigRes, musicEventsRes, bandPhotosRes, gamesRes, shopBannersRes] = await Promise.all([
        axios.get(`${API}/shows`).catch(() => ({ data: [] })),
        axios.get(`${API}/seasons`),
        axios.get(`${API}/videos`),
        axios.get(`${API}/shop-products`).catch(() => ({ data: [] })),
        axios.get(`${API}/banners`).catch(() => ({ data: [] })),
        axios.get(`${API}/phrases`).catch(() => ({ data: [] })),
        axios.get(`${API}/tickers`).catch(() => ({ data: [] })),
        axios.get(`${API}/music/config`).catch(() => ({ data: {} })),
        axios.get(`${API}/music/events`).catch(() => ({ data: [] })),
        axios.get(`${API}/music/photos`).catch(() => ({ data: [] })),
        axios.get(`${API}/games`).catch(() => ({ data: [] })),
        axios.get(`${API}/shop-banners`).catch(() => ({ data: [] }))
      ]);
      setShows(showsRes.data);
      setSeasons(seasonsRes.data);
      setVideos(videosRes.data);
      setShopProducts(productsRes.data);
      setBanners(bannersRes.data);
      setPhrases(phrasesRes.data);
      setTickers(tickersRes.data);
      setMusicConfig(musicConfigRes.data);
      setMusicEvents(musicEventsRes.data);
      setBandPhotos(bandPhotosRes.data);
      setGames(gamesRes.data);
      setShopBanners(shopBannersRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500 font-medium">Verificando autenticación...</div>
      </div>
    );
  }

  // Main sections
  const sections = [
    { id: "videos", label: "Videos", icon: Film },
    { id: "juegos", label: "Juegos", icon: Gamepad2 },
    { id: "musica", label: "Música", icon: Music },
    { id: "tienda", label: "Tienda", icon: ShoppingBag },
    { id: "media-kit", label: "Media Kit", icon: FileText },
    { id: "promocion", label: "Promoción", icon: Megaphone },
    { id: "app", label: "App", icon: Smartphone },
    { id: "comunidad", label: "Comunidad", icon: Users },
    { id: "sitio", label: "Sitio", icon: Globe },
  ];

  // Sub-tabs per section
  const subTabs = {
    videos: [
      { id: "contenido", label: "Contenido", icon: Tv, count: shows.length },
      { id: "import", label: "Importar", icon: Upload, count: null },
    ],
    promocion: [
      { id: "promo-banner", label: "Banner Promo", icon: Megaphone, count: null },
      { id: "navbar-banner", label: "Banner Navbar", icon: Image, count: null },
      { id: "preroll", label: "Preroll Ads", icon: Film, count: null },
      { id: "mobile-banners", label: "Banners Móvil", icon: Image, count: null },
      { id: "desktop-banners", label: "Banners Desktop", icon: Image, count: null },
      { id: "sidebar-ads", label: "Sidebar Ads", icon: Image, count: null },
      { id: "banners", label: "Banners Home", icon: Image, count: banners.length },
      { id: "ticker", label: "Ticker", icon: Megaphone, count: tickers.length },
      { id: "phrases", label: "Frases", icon: MessageCircle, count: phrases.length },
    ],
    tienda: [
      { id: "banners-tienda", label: "Banners", icon: Image, count: shopBanners.length },
      { id: "orden-productos", label: "Orden", icon: GripVertical, count: null },
    ],
    musica: [
      { id: "config", label: "Configuración", icon: Settings, count: null },
      { id: "thumbnails", label: "Thumbnails", icon: Image, count: null },
      { id: "eventos", label: "Eventos", icon: Calendar, count: musicEvents.length },
      { id: "fotos", label: "Galería", icon: Camera, count: bandPhotos.length },
    ],
    juegos: [
      { id: "gestor", label: "Gestor", icon: Gamepad2, count: games.length },
    ],
    "media-kit": [
      { id: "mk-logos", label: "Logos Clientes", icon: Building2, count: null },
      { id: "mk-videos", label: "Videos", icon: Video, count: null },
      { id: "mk-productos", label: "Productos Licenciados", icon: Package, count: null },
      { id: "mk-casos-exito", label: "Casos de Éxito", icon: Trophy, count: null },
      { id: "mk-conciertos", label: "Fotos Conciertos", icon: Camera, count: null },
      { id: "mk-spotify", label: "Cifras Spotify", icon: Music, count: null },
    ],
    sitio: [
      { id: "settings", label: "Configuración", icon: Settings, count: null },
    ],
    app: [
      { id: "stickers", label: "Stickers", icon: MessageCircle, count: null },
      { id: "notificaciones", label: "Notificaciones", icon: Bell, count: null },
    ],
    comunidad: [
      { id: "comentarios", label: "Comentarios", icon: MessageCircle, count: null },
      { id: "usuarios", label: "Usuarios", icon: Users, count: null },
      { id: "avatares", label: "Avatares", icon: Image, count: null },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 text-black relative z-10 flex flex-col" data-testid="admin-page">
      {/* Header */}
      <header className="bg-black px-4 md:px-6 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <a 
              href="/" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft size={20} />
              <span className="font-medium hidden sm:inline">Volver</span>
            </a>
            <h1 className="text-lg md:text-2xl font-bold text-white">Panel de <span className="text-[#E91E8C]">Administración</span></h1>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <span className="text-sm text-white/60 hidden sm:inline">{adminEmail}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPasswordModal(true)}
              className="text-white/80 hover:text-white hover:bg-white/10 p-2 md:px-3"
              data-testid="change-password-btn"
            >
              <Lock size={18} />
              <span className="hidden md:inline ml-2">Contraseña</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/80 hover:text-white hover:bg-white/10 p-2 md:px-3"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline ml-2">Salir</span>
            </Button>
          </div>
        </div>
      </header>

      <ChangePasswordModal 
        isOpen={showPasswordModal} 
        onClose={() => setShowPasswordModal(false)} 
      />

      <main className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8 flex-1 pb-8 overflow-x-hidden box-border">
        {/* Main Section Tabs - Scrollable on mobile */}
        <div className="overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4" style={{ touchAction: 'pan-x' }}>
          <div className="inline-flex gap-2 mb-4 md:mb-6">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-t-xl font-bold text-sm md:text-lg transition-colors whitespace-nowrap ${
                  activeSection === section.id 
                    ? "bg-white text-black border-t-4 border-x border-[#E91E8C]" 
                    : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
                data-testid={`section-${section.id}`}
              >
                <section.icon size={18} className="md:w-[22px] md:h-[22px] flex-shrink-0" />
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="bg-white rounded-b-xl rounded-tr-xl shadow-sm border border-gray-200 p-4 md:p-6 overflow-hidden">
          <div className="flex gap-2 md:gap-3 mb-4 md:mb-6 flex-wrap border-b border-gray-200 pb-4 overflow-x-auto scrollbar-hide">
            {subTabs[activeSection]?.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap text-sm ${
                  activeSubTab === tab.id 
                    ? "bg-black text-white" 
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`subtab-${tab.id}`}
              >
                <tab.icon size={16} className="md:w-[18px] md:h-[18px]" />
                {tab.label} {tab.count !== null && <span className="text-xs opacity-70">({tab.count})</span>}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeSection === "videos" && (
            <>
              {activeSubTab === "contenido" && (
                <ContentManagerTab 
                  shows={shows}
                  seasons={seasons}
                  videos={videos}
                  onRefresh={fetchData}
                />
              )}
              {activeSubTab === "import" && (
                <BulkImportTab shows={shows} seasons={seasons} onRefresh={fetchData} />
              )}
            </>
          )}

          {activeSection === "promocion" && (
            <>
              {activeSubTab === "promo-banner" && (
                <PromoBannerTab onRefresh={fetchData} />
              )}
              {activeSubTab === "navbar-banner" && (
                <NavbarBannerTab onRefresh={fetchData} />
              )}
              {activeSubTab === "preroll" && (
                <PrerollTab onRefresh={fetchData} />
              )}
              {activeSubTab === "mobile-banners" && (
                <MobileBannerTab onRefresh={fetchData} />
              )}
              {activeSubTab === "desktop-banners" && (
                <DesktopBannerTab onRefresh={fetchData} />
              )}
              {activeSubTab === "sidebar-ads" && (
                <SidebarAdsTab onRefresh={fetchData} />
              )}
              {activeSubTab === "banners" && (
                <BannersTab banners={banners} onRefresh={fetchData} />
              )}
              {activeSubTab === "ticker" && (
                <TickersTab tickers={tickers} onRefresh={fetchData} />
              )}
              {activeSubTab === "phrases" && (
                <PhrasesTab phrases={phrases} onRefresh={fetchData} />
              )}
            </>
          )}

          {activeSection === "tienda" && (
            <>
              {activeSubTab === "banners-tienda" && (
                <ShopBannersTab banners={shopBanners} onRefresh={fetchData} />
              )}
              {activeSubTab === "orden-productos" && (
                <ShopProductOrderTab />
              )}
            </>
          )}

          {activeSection === "musica" && (
            <>
              {activeSubTab === "config" && (
                <MusicConfigTab config={musicConfig} onRefresh={fetchData} />
              )}
              {activeSubTab === "thumbnails" && (
                <MusicThumbnailsTab onRefresh={fetchData} />
              )}
              {activeSubTab === "eventos" && (
                <MusicEventsTab events={musicEvents} onRefresh={fetchData} />
              )}
              {activeSubTab === "fotos" && (
                <BandPhotosTab photos={bandPhotos} onRefresh={fetchData} />
              )}
            </>
          )}

          {activeSection === "juegos" && (
            <>
              {activeSubTab === "gestor" && (
                <GamesManagerTab games={games} onRefresh={fetchData} />
              )}
            </>
          )}

          {activeSection === "media-kit" && (
            <>
              {activeSubTab === "mk-logos" && (
                <MediaKitLogosTab onRefresh={fetchData} />
              )}
              {activeSubTab === "mk-videos" && (
                <MediaKitVideosTab onRefresh={fetchData} />
              )}
              {activeSubTab === "mk-productos" && (
                <MediaKitProductsTab onRefresh={fetchData} />
              )}
              {activeSubTab === "mk-casos-exito" && (
                <MediaKitSuccessCasesTab onRefresh={fetchData} />
              )}
              {activeSubTab === "mk-conciertos" && (
                <MediaKitConcertPhotosTab />
              )}
              {activeSubTab === "mk-spotify" && (
                <MediaKitMusicStatsTab />
              )}
            </>
          )}

          {activeSection === "sitio" && (
            <>
              {activeSubTab === "settings" && (
                <SiteSettingsTab onRefresh={fetchData} />
              )}
            </>
          )}

          {activeSection === "app" && (
            <>
              {activeSubTab === "stickers" && (
                <StickersTab />
              )}
              {activeSubTab === "notificaciones" && (
                <NotificationsTab />
              )}
            </>
          )}

          {activeSection === "comunidad" && (
            <>
              {activeSubTab === "comentarios" && <CommentsModerationTab />}
              {activeSubTab === "usuarios" && <FansTab />}
              {activeSubTab === "avatares" && <AvatarsTab />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
